import { FC, memo, MouseEvent, useEffect } from 'react';
import { DeleteNodeModal } from '@components/MainStage/ContextMenu/NodeContextMenu/DeleteNodeModal';
import { isFocusEligible } from '@components/MainStage/focusOn.helpers';
import {
  useIsFocusActive,
  useIsNodeFocused,
} from '@components/MainStage/focusOn.store';
import useExpandCollapsedLayers from '@components/MainStage/hooks/useExpandCollapsedLayers';
import useFocusOn from '@components/MainStage/hooks/useFocusOn';
import useIsNodeAddedToAsset from '@components/MainStage/hooks/useIsNodeAddedToAsset';
import useMultiSelectEntity from '@components/MainStage/hooks/useMultiSelectEntity';
import {
  getHoverState,
  useIsNodeHovered,
} from '@components/MainStage/hover.store';
import { useSearchNeedle } from '@components/MainStage/listSearch.store';
import {
  useIsNodeMultiSelected,
  useMultiSelectionCount,
} from '@components/MainStage/multiSelection.store';
import useStore from '@components/MainStage/store';
import { LayerTypes, SubLayerTypes } from '@constants/canvas/layers';
import {
  AddNodeDrawerMap,
  EditNodeDrawerMap,
} from '@constants/entities/drawers';
import { useProject } from '@context/Project/ProjectProvider';
import useCenterNodeViewport from '@hooks/canvas/useCenterNodeViewport';
import useCanEditModel from '@hooks/useCanEditModel';
import useNode from '@hooks/useNode';
import useToggle from '@hooks/useToggle';
import {
  Checkbox,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { NodeDTOTypes } from '@store/services/nodes/types';
import { themePalette } from '@theme/muiTheme';
import {
  getOutlinedInitiativeNodeIcon,
  getOutlinedNodeIcon,
} from '@utils/canvasIconHelpers';
import { isElementLocal } from '@utils/drawers';
import {
  CheckBoxCheckedFilledIcon,
  CloseIcon,
  FocusOnIcon,
  MoreIcon,
  TrashIcon,
} from '@utils/iconsDefs';
import { renderHighlightedText } from '@utils/searchHighlight';
import useIsActionInSubLayerAllowed from '@views/Project/components/ListMenu/hooks/useIsActionInSubLayerAllowed';
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';

type Props = {
  layerId: LayerTypes;
  subLayerId: SubLayerTypes;
  id: string;

  title: string;
};

const hoverRevealStyles = {
  '& .type-icon': { display: 'none' },
  '& .hover-checkbox': { display: 'inline-flex' },
  '& .more': { opacity: 1 },
};

const drawerOpenHoverRevealStyles = {
  '& .more': { opacity: 1 },
};

const NodeItem: FC<Props> = ({ layerId, id, subLayerId, title }) => {
  const { selectNode, getActiveNode, nodes } = useStore();
  const { drawer, setDrawer, handleCloseDrawer } = useProject();
  const { selectEntity, clearMultiSelection, setSingleSelectionAnchor } =
    useMultiSelectEntity();
  const { expandForNode } = useExpandCollapsedLayers();
  const [isDeleteOpen, { toggle, off }] = useToggle();
  const showMoreButton = useCanEditModel();
  const { isNodeActionAllowed } = useIsActionInSubLayerAllowed();

  const isNodeMultiSelected = useIsNodeMultiSelected(id);
  const isNodeHovered = useIsNodeHovered(id);
  const isNodeActive = id === getActiveNode()?.id || isNodeMultiSelected;
  const searchNeedle = useSearchNeedle();
  const isNodeAdded = useIsNodeAddedToAsset(id);
  const node = useNode(id);

  const multiSelectionCount = useMultiSelectionCount();
  const { enterFocus, exitFocus } = useFocusOn();
  const isFocusActive = useIsFocusActive();
  const isNodeInFocus = useIsNodeFocused(id);

  const isDrawerOpen = Boolean(drawer);
  const revealStyles = isDrawerOpen
    ? drawerOpenHoverRevealStyles
    : hoverRevealStyles;

  const nodeType = node?.data?.dto?.type;
  const typeIcon =
    nodeType === NodeDTOTypes.Initiatives
      ? getOutlinedInitiativeNodeIcon({
          type: node?.data?.dto?.meta_data?.type,
          width: '20px',
          height: '20px',
        })
      : getOutlinedNodeIcon({ type: nodeType, width: '20px', height: '20px' });

  const popupState = usePopupState({
    variant: 'popover',
    popupId: id,
  });

  const centerNode = useCenterNodeViewport();

  useEffect(() => {
    if (!popupState.isOpen || (multiSelectionCount > 1 && isNodeMultiSelected))
      return undefined;

    getHoverState().setPinned(id);

    return () => getHoverState().unpin(id);
  }, [popupState.isOpen, multiSelectionCount, isNodeMultiSelected, id]);

  const handleOpenDrawer = async () => {
    if (isFocusActive && !isNodeInFocus) return;
    if (!isNodeActionAllowed(subLayerId as SubLayerTypes)) return;

    clearMultiSelection();

    await handleCloseDrawer();
    selectNode(id);

    const drawersMap = isElementLocal(id)
      ? AddNodeDrawerMap
      : EditNodeDrawerMap;

    setDrawer({ type: drawersMap[layerId] });
    // Remember this entity so a following Shift-click keeps it selected.
    setSingleSelectionAnchor(id);

    if (node) {
      centerNode(node.position, subLayerId);
    }
  };

  const handleItemClick = async (event: MouseEvent) => {
    if (event.shiftKey && !isElementLocal(id)) {
      // No multi-selection while a drawer is open, and Shift+click must not fall
      // through to opening another entity either.
      if (isDrawerOpen) return;

      // Multi-select reveals a collapsed layer instead of blocking the action,
      // so we intentionally skip the collapsed-layer guard here.
      await expandForNode(layerId, subLayerId);
      await selectEntity(id);
      return;
    }

    handleOpenDrawer();
  };

  const handleCheckboxClick = (event: MouseEvent) => {
    // Only the checkbox toggles selection; stop the row's onClick (drawer open).
    event.stopPropagation();
  };

  const handleCheckboxToggle = async () => {
    if (isElementLocal(id) || isDrawerOpen) return;

    // Checking a node that lives inside a collapsed layer/sub-layer reveals it on
    // the canvas first, so the selection is visible (mirrors Shift+click entry).
    if (!isNodeMultiSelected) {
      await expandForNode(layerId, subLayerId);
    }

    // selectEntity handles both entering (add + drawer unsaved-changes) and,
    // when a selection is active, toggling (add if new / deselect if present).
    await selectEntity(id);
  };

  const { onClick, ...triggerProps } = bindTrigger(popupState);

  const handleMoreClick = (e: MouseEvent) => {
    e.stopPropagation();
    onClick(e);
  };

  const handleDelete = async () => {
    popupState.close();
    if (!isNodeActionAllowed(subLayerId as SubLayerTypes)) return;

    await handleCloseDrawer();

    toggle();
  };

  return (
    <>
      <DeleteNodeModal open={isDeleteOpen} onClose={off} nodeId={id} />

      <MenuItem
        sx={{
          justifyContent: 'space-between',
          cursor: 'pointer',
          '& .hover-checkbox': { display: 'none' },
          '&:hover': revealStyles,
          ...(isNodeHovered && {
            backgroundColor: themePalette.grey[400],
            ...revealStyles,
          }),
          ...(isNodeMultiSelected && {
            borderRight: `2px solid ${themePalette.green[900]}`,
          }),
        }}
        disableRipple
        onClick={handleItemClick}
        onMouseEnter={() => getHoverState().setHovered(id)}
        onMouseLeave={() => getHoverState().unhover(id)}
      >
        <Stack
          gap={2}
          direction="row"
          alignItems="center"
          sx={{ width: '100%', overflow: 'hidden' }}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ width: 20, height: 20, flexShrink: 0 }}
          >
            {isNodeMultiSelected ? (
              <Checkbox
                checked
                disableRipple
                onClick={handleCheckboxClick}
                onChange={handleCheckboxToggle}
                checkedIcon={<CheckBoxCheckedFilledIcon />}
                inputProps={{ 'aria-label': `Select ${title}` }}
              />
            ) : (
              <>
                <Stack
                  className="type-icon"
                  alignItems="center"
                  justifyContent="center"
                >
                  {typeIcon}
                </Stack>
                {!isDrawerOpen && (
                  <Checkbox
                    className="hover-checkbox"
                    checked={false}
                    disableRipple
                    onClick={handleCheckboxClick}
                    onChange={handleCheckboxToggle}
                    inputProps={{ 'aria-label': `Select ${title}` }}
                  />
                )}
              </>
            )}
          </Stack>

          <Typography
            variant="body1"
            color={themePalette.grey[isNodeActive ? 1000 : 800]}
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              width: '100%',
              whiteSpace: 'nowrap',
            }}
          >
            {renderHighlightedText(title, searchNeedle, 'inherit', true)}
          </Typography>
        </Stack>
        {showMoreButton && (
          <IconButton
            className="more"
            sx={{
              opacity: popupState.isOpen ? 1 : 0,
              borderRadius: '4px',
              borderColor: popupState.isOpen
                ? `${themePalette.grey[900]} !important`
                : themePalette.grey[700],

              '&:hover': {
                borderColor: `${themePalette.grey[900]} !important`,

                '& .icon': {
                  fill: `${themePalette.grey[1000]} !important`,
                },
              },
            }}
            size="small"
            variant="simple"
            {...triggerProps}
            onClick={handleMoreClick}
          >
            <MoreIcon
              width={16}
              height={16}
              fill={
                popupState.isOpen
                  ? themePalette.grey[1000]
                  : themePalette.grey[800]
              }
              className="icon"
            />
          </IconButton>
        )}
      </MenuItem>

      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 220, mt: 4 } } }}
      >
        {isFocusActive && isNodeInFocus && (
          <MenuItem
            sx={{ color: themePalette.grey[1000] }}
            onClick={() => {
              popupState.close();
              exitFocus();
            }}
          >
            <FocusOnIcon fill="currentColor" />
            Exit focus on
          </MenuItem>
        )}

        {popupState.isOpen &&
          !isFocusActive &&
          node &&
          isFocusEligible(node, multiSelectionCount, nodes) && (
            <MenuItem
              sx={{ color: themePalette.grey[1000] }}
              onClick={() => {
                popupState.close();
                enterFocus(id, node.data.dto.name);
              }}
            >
              <FocusOnIcon fill="currentColor" />
              Focus on
            </MenuItem>
          )}

        {isNodeAdded ? (
          <MenuItem
            sx={{ color: themePalette.grey[1000] }}
            onClick={handleDelete}
          >
            <CloseIcon fill="currentColor" />
            Remove entity
          </MenuItem>
        ) : (
          <MenuItem
            sx={{
              color: themePalette.system.error,
              '&:hover': { color: themePalette.system.error },
            }}
            onClick={handleDelete}
          >
            <TrashIcon />
            Delete
          </MenuItem>
        )}
      </Popover>
    </>
  );
};

export default memo(NodeItem);
